import { FC, useCallback, useContext } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  List,
  ListItem,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import { isEmpty } from "lodash";
import __ from "src/utils/Translations";
import { GlobalModalContext } from "src/utils/GlobalModal/GlobalModalContext";
import useBasePath from "src/utils/hooks/useBasePath";
import { usePermission } from "src/utils/hooks/permissions/usePermissions";
import { absencesAbsencesConfigurationWrite } from "src/utils/RoleBasedSecurity/permissions";
import AbsenceGroupsEmpty from "src/Pages/Company/Absences/AbsenceGroups/AbsenceGroupsEmpty";
import ActionButton from "src/Pages/Company/Absences/ActionButton/ActionButton";
import OffIcon from "src/theme/Icons/Off";
import OnIcon from "src/theme/Icons/On";
import { EPresenceType } from "src/Pages/Company/Absences/AbsenceType/AbsenceType.models";
import { IAbsenceGroup } from "./AbsenceGroups.models";

interface IAbsenceGroupsListProps {
  data: IAbsenceGroup[];
  isFetching: boolean;
  toggleActivation: (
    groupId: string,
    groupName: string,
    active: boolean
  ) => void;
}

const AbsenceGroupsList: FC<IAbsenceGroupsListProps> = ({
  data = [],
  isFetching,
  toggleActivation,
}) => {
  const history = useHistory();
  const basePath = useBasePath();
  const canWriteAbsencesConfiguration = usePermission(
    absencesAbsencesConfigurationWrite
  );
  const { setModal, resetModal } = useContext(GlobalModalContext);

  const confirmDeactivation = useCallback(
    (group: IAbsenceGroup) => {
      if (typeof setModal === "function") {
        setModal({
          open: true,
          title: (
            <>
              {__("Czy na pewno chcesz dezaktywować grupę")} {group.name}?
            </>
          ),
          description: `${__("Dezaktywując grupę")} ${group.name} ${__(
            'przeniesiesz ją do zakładki "Nieaktywne". W każdej chwili będziesz mógł ją ponownie aktywować.'
          )}`,
          actions: [
            {
              label: __("Tak, dezaktywuję"),
              variant: "contained",
              callbackFn: () => {
                toggleActivation(group.id, group.name, false);
                resetModal();
              },
            },
          ],
        });
      }
    },
    [resetModal, setModal, toggleActivation]
  );

  const confirmActivation = useCallback(
    (group: IAbsenceGroup) => {
      if (typeof setModal === "function") {
        setModal({
          open: true,
          title: (
            <>
              {__("Czy na pewno chcesz aktywować grupę")} {group.name}?
            </>
          ),
          actions: [
            {
              label: __("Tak, aktywuję"),
              callbackFn: () => {
                toggleActivation(group.id, group.name, true);
                resetModal();
              },
            },
          ],
        });
      }
    },
    [resetModal, setModal, toggleActivation]
  );

  if (isEmpty(data) && !isFetching) {
    return <AbsenceGroupsEmpty />;
  }

  return (
    <Box
      sx={{ bgcolor: "grey.100", padding: "1rem 2rem", borderRadius: "1rem" }}
    >
      <List data-testid="absence-groups-list">
        {(isFetching ? Array.from(new Array(3)) : data).map((item, index) => (
          <ListItem
            key={item?.id || index}
            data-testid="absence-groups-list__item"
          >
            <Stack
              direction="row"
              alignItems="center"
              sx={{ width: "100%", margin: "1rem 0" }}
            >
              {item ? (
                <>
                  <Typography variant="subtitle1">{item.order}</Typography>
                  <BookmarkIcon
                    sx={{
                      fontSize: "2rem",
                      color: item.color,
                      margin: "1rem 1rem 1rem 2rem",
                    }}
                  />
                </>
              ) : (
                <Skeleton variant="rectangular" width={78} height={64} />
              )}
              {item ? (
                <>
                  <Box width="75%">
                    <Typography
                      variant="subtitle1"
                      component="h4"
                      data-testid="absence-groups-list__item-name"
                    >
                      {item.name}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {item.description}
                    </Typography>
                    {item.presenceType && (
                      <Box sx={{ mt: "0.5rem" }}>
                        <Typography
                          variant="body2"
                          color="info.dark"
                          component="span"
                          mr="0.5rem"
                        >
                          &bull;
                        </Typography>
                        <Typography
                          variant="body2"
                          component="span"
                          sx={{ fontWeight: "bold" }}
                          data-testid="absence-group-presence-type"
                        >
                          {item.presenceType === EPresenceType.Absence &&
                            __("Nieobecność")}
                          {item.presenceType === EPresenceType.Attendance &&
                            __("Status obecności")}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </>
              ) : (
                <Box width="75%" ml="1rem">
                  <Skeleton width="40%" />
                  <Skeleton />
                </Box>
              )}
              {canWriteAbsencesConfiguration && (
                <Box
                  sx={{
                    flexShrink: 0,
                    marginLeft: "auto",
                  }}
                >
                  {item?.active ? (
                    <>
                      <ActionButton
                        label={__("Edytuj")}
                        bgColor="info.main"
                        bgColorHover="info.dark"
                        color="white"
                        margin="0.5rem"
                        disabled={isFetching}
                        onClick={() =>
                          history.push(
                            `${basePath}/absences/absence-groups/${item.id}`
                          )
                        }
                        data-testid="absence-groups-list__button-edit"
                      >
                        <EditNoteOutlinedIcon />
                      </ActionButton>
                      <ActionButton
                        label={__("Dezaktywuj")}
                        bgColor="warning.main"
                        bgColorHover="warning.dark"
                        color="white"
                        margin="0.5rem"
                        disabled={isFetching}
                        onClick={() => {
                          confirmDeactivation(item);
                        }}
                        data-testid="absence-groups-list__button-deactivate"
                      >
                        <OffIcon />
                      </ActionButton>
                    </>
                  ) : (
                    <ActionButton
                      label={__("Aktywuj")}
                      bgColor="success.main"
                      bgColorHover="success.dark"
                      color="white"
                      margin="0.5rem"
                      disabled={isFetching}
                      onClick={() => {
                        confirmActivation(item);
                      }}
                      data-testid="absence-groups-list__button-activate"
                    >
                      <OnIcon />
                    </ActionButton>
                  )}
                </Box>
              )}
            </Stack>
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

export default AbsenceGroupsList;
